import React, { useEffect, useState, useMemo, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalIcon, Clock, CalendarClock, Loader2 } from "lucide-react";
import { ReminderDialog } from "@/components/ReminderDialog";
import { fmtDateTimeIT } from "@/lib/format";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { TableSkeleton, CardSkeleton } from "@/components/SkeletonLoader";

const WEEK_DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS_IT = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

function buildGrid(year, month) {
  // month 0-indexed
  const first = new Date(year, month, 1);
  // shift to Monday start (1=Mon..0=Sun)
  let firstWeekday = first.getDay();
  firstWeekday = firstWeekday === 0 ? 6 : firstWeekday - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const ymd = (d) => d.toLocaleDateString("sv-SE");

export default function CalendarPage() {
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRem, setShowRem] = useState(false);
  const [showAppt, setShowAppt] = useState(false);
  const [presetWhen, setPresetWhen] = useState(null);
  const [editing, setEditing] = useState(null);  // reminder selezionato per click
  const [dragId, setDragId] = useState(null);
  const nav = useNavigate();

  const range = useMemo(() => {
    const start = new Date(view.y, view.m, 1);
    const end = new Date(view.y, view.m + 1, 0);
    return { from: ymd(start), to: ymd(end) };
  }, [view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reminders", {
        params: { fromDate: range.from, toDate: range.to + "T23:59:59", limit: 2000 },
      });
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => buildGrid(view.y, view.m), [view]);
  const byDay = useMemo(() => {
    const m = {};
    for (const it of items) {
      const k = (it.when || "").slice(0, 10);
      if (!k) continue;
      (m[k] ||= []).push(it);
    }
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.when.localeCompare(b.when)));
    return m;
  }, [items]);

  const move = useCallback(async (id, newDateStr) => {
    const r = items.find((x) => x.id === id);
    if (!r) return;
    const oldTime = r.when?.slice(11, 16) || "10:00";
    const newWhen = `${newDateStr}T${oldTime}:00`;
    try {
      await api.patch(`/reminders/${id}`, { when: newWhen.slice(0, 16) });
      toast.success(`Spostato al ${newDateStr}`);
      load();
    } catch { toast.error("Errore spostamento"); }
  }, [items, load]);

  const monthLabel = `${MONTHS_IT[view.m]} ${view.y}`;
  const goPrev = () => setView((v) => v.m === 0 ? { y: v.y - 1, m: 11 } : { ...v, m: v.m - 1 });
  const goNext = () => setView((v) => v.m === 11 ? { y: v.y + 1, m: 0 } : { ...v, m: v.m + 1 });
  const goToday = () => setView({ y: today.getFullYear(), m: today.getMonth() });

  const newAt = (dateStr, isAppt = false) => {
    setPresetWhen(`${dateStr}T10:00`);
    if (isAppt) setShowAppt(true); else setShowRem(true);
  };

  return (
    <AppLayout
      title="Calendario"
      subtitle={monthLabel}
      actions={
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white border border-slate-200 rounded-[3px]">
            <button onClick={goPrev} className="p-2 hover:bg-slate-50 text-slate-700"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={goToday} className="px-3 py-2 text-sm border-x border-slate-200 hover:bg-slate-50">Oggi</button>
            <button onClick={goNext} className="p-2 hover:bg-slate-50 text-slate-700"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <button onClick={() => nav("/agenda")} className="px-3 py-2 text-sm border border-slate-300 rounded-[3px] hover:bg-slate-50 text-slate-700 bg-white">Vista lista</button>
          <button onClick={() => { setPresetWhen(null); setShowRem(true); }} data-testid="cal-new-rem" className="text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"}><Plus className="w-4 h-4" /> Promemoria</button>
          <button onClick={() => { setPresetWhen(null); setShowAppt(true); }} data-testid="cal-new-appt" className="bg-[#B7955B] hover:bg-[#A18048] text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2"><CalIcon className="w-4 h-4" /> Appuntamento</button>
        </div>
      }
    >
      <div className="bg-white border border-slate-200 rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)] overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {WEEK_DAYS.map((d) => (
            <div key={d} className="px-3 py-2.5 text-[10px] uppercase tracking-[0.1em] text-slate-500 font-semibold text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7" data-testid="cal-grid">
          {cells.map((d, idx) => {
            const isToday = d && ymd(d) === ymd(today);
            const isWeekend = idx % 7 >= 5;
            const dayKey = d ? ymd(d) : "";
            const dayItems = d ? (byDay[dayKey] || []) : [];
            return (
              <div
                key={idx}
                onDragOver={d ? (e) => { e.preventDefault(); } : undefined}
                onDrop={d && dragId ? (e) => { e.preventDefault(); move(dragId, dayKey); setDragId(null); } : undefined}
                className={`min-h-[120px] p-2 border-r border-b border-[#F0F2F5] ${isWeekend ? "bg-slate-50/40" : "bg-white"} ${!d ? "bg-slate-50/30" : ""} ${dragId && d ? "hover:bg-amber-50/60" : ""}`}
                data-testid={d ? `cal-day-${dayKey}` : undefined}
              >
                {d && (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <button
                        onDoubleClick={() => newAt(dayKey, false)}
                        className={`text-xs font-semibold tabular-nums ${isToday ? "bg-[#0A192F] text-white px-2 py-0.5 rounded-full" : "text-slate-700"}`}
                      >
                        {d.getDate()}
                      </button>
                      {dayItems.length > 0 && <span className="text-[10px] text-slate-400 tabular-nums">{dayItems.length}</span>}
                    </div>
                    <div className="space-y-1">
                      {dayItems.slice(0, 4).map((r) => {
                        const isAppt = !!r.appointment;
                        const time = r.when ? r.when.slice(11, 16) : "";
                        return (
                          <div
                            key={r.id}
                            draggable
                            onDragStart={() => setDragId(r.id)}
                            onDragEnd={() => setDragId(null)}
                            onClick={(e) => { e.stopPropagation(); setEditing(r); if (isAppt) setShowAppt(true); else setShowRem(true); }}
                            className={`text-[11px] px-1.5 py-1 rounded-[3px] border cursor-pointer hover:ring-1 hover:ring-[#B7955B]/50 truncate ${
                              r.completed
                                ? "bg-slate-100 text-slate-400 line-through border-slate-200"
                                : isAppt
                                  ? "bg-[#F5EFE3] text-[#8a6e3d] border-[#E5D7B6]"
                                  : "bg-sky-50 text-sky-700 border-sky-200"
                            }`}
                            title={`Click per aprire · ${r.title}`}
                            data-testid={`cal-item-${r.id}`}
                          >
                            <span className="tabular-nums font-semibold mr-1">{time}</span>
                            <span>{r.title}</span>
                          </div>
                        );
                      })}
                      {dayItems.length > 4 && <div className="text-[10px] text-slate-500">+{dayItems.length - 4} altri</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {loading && <div className="px-4 py-2 text-xs text-slate-500 flex items-center gap-2 border-t border-[#F0F2F5]"><Loader2 className="w-3 h-3 animate-spin" /> Caricamento…</div>}
        <div className="px-4 py-2.5 text-[11px] text-slate-500 border-t border-[#F0F2F5] flex items-center gap-4">
          <span className="flex items-center gap-1.5 text-[#8a6e3d]"><CalendarClock className="w-3 h-3" /> Appuntamenti</span>
          <span className="flex items-center gap-1.5 text-sky-600"><Clock className="w-3 h-3" /> Promemoria</span>
          <span className="ml-auto">Trascina un evento per spostarlo • Doppio click su un giorno per aggiungere</span>
        </div>
      </div>

      <ReminderDialog
        open={showRem}
        onClose={() => { setShowRem(false); setEditing(null); }}
        onSaved={() => { setShowRem(false); setEditing(null); load(); }}
        appointment={false}
        initial={editing ? editing : (presetWhen ? { when: presetWhen + ":00" } : undefined)}
      />
      <ReminderDialog
        open={showAppt}
        onClose={() => { setShowAppt(false); setEditing(null); }}
        onSaved={() => { setShowAppt(false); setEditing(null); load(); }}
        appointment={true}
        initial={editing ? editing : (presetWhen ? { when: presetWhen + ":00" } : undefined)}
      />
    </AppLayout>
  );
}
